<template>
  <div class="bg-base-200 rounded-lg">
    <VanField
      v-model="event.name"
      :label="$t('Chats.chatScene.edit.name')"
      :placeholder="$t('Chats.chatScene.edit.eventPlaceholder')"
      :rules="[
        {
          required: true,
          message: $t('Chats.chatScene.edit.eventPlaceholder'),
        },
      ]"
      required
      @blur="saveEvent('name')"
    >
      <template #label>
        <span>
          {{ $t("Chats.chatScene.edit.name") }}
        </span>
        <i
          class="iconfont icon-info text-xs ml-1 cursor-pointer"
          @click="clickNameInfo"
        />
      </template>
    </VanField>
    <VanField
      :model-value="event.probability + '%'"
      is-link
      readonly
      :label="$t('Chats.chatScene.edit.probability')"
      required
      @click="clickOpenScoreSliderModal()"
    />
    <div class="flex flex-col gap-2 py-2">
      <div class="flex pl-4">
        <span class="text-[#ee0a24] mr-[2px]">*</span>
        <span>
          {{ $t("Chats.chatScene.edit.definition") }}
        </span>
      </div>
      <GenerableTextArea
        class="pl-6 pr-4"
        v-model="event.definition"
        :enable-generate="false"
        :label="$t('User.mate.chat.systemPrompt')"
        :mateContext="mateContext"
        :promptType="MateCardPromptType.Greeting"
        @blur="saveEvent('definition')"
      />
    </div>

    <!-- 管理 -->
    <div class="flex justify-end gap-2 pr-4 pb-2 mb-1">
      <button
        v-if="idx !== 0"
        class="btn btn-neutral btn-xs rounded text-base-content/50"
        @click="clickSort('up')"
      >
        <i class="iconfont icon-up text-xs"></i>
      </button>
      <button
        v-if="!isBottom"
        class="btn btn-neutral btn-xs rounded text-base-content/50"
        @click="clickSort('down')"
      >
        <i class="iconfont icon-down text-xs"></i>
      </button>
      <button
        class="btn btn-neutral btn-xs rounded text-base-content/50"
        @click="clickDelete"
      >
        <i class="iconfont icon-delete text-xs"></i>
      </button>
    </div>
  </div>

  <BaseScoreSliderModal
    v-model:show="showScoreSliderModal"
    :title="t('Chats.chatScene.edit.probability')"
    :score="showScore"
    :min="0"
    @confirm="confirmScore"
  />
</template>

<script setup lang="ts">
import {
  createOneChatSceneEvent,
  updateOneChatSceneEvent,
  updateOneChatSceneEventSort,
} from "@/apis/chatScene";
import { MateCardPromptType } from "@/enums/chatEnums";
import { useChatScene } from "@/stores/chatScene";
import { ChatSceneEvent, GenerateMateContext } from "@/types/chat";
import { showDialog } from "vant";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const chatSceneStore = useChatScene();
const { t } = useI18n();
const props = defineProps<{
  event: ChatSceneEvent;
  mateContext: GenerateMateContext;
  idx: number;
  isBottom: boolean;
}>();

const emit = defineEmits<{
  (e: "update:sort", payload: { idx: number; direction: "up" | "down" }): void;
  (e: "update:delete", idx: number): void;
}>();

const clickNameInfo = () => {
  showDialog({
    message: t("Chats.chatScene.edit.eventNameInfo"),
    confirmButtonText: t("Buttons.confirm"),
  });
};

const showScoreSliderModal = ref(false);
const showScore = ref(0);
const clickOpenScoreSliderModal = () => {
  showScoreSliderModal.value = true;
  showScore.value = props.event.probability ?? 0;
};

const confirmScore = (score: number) => {
  showScoreSliderModal.value = false;
  props.event.probability = score;
  saveEvent("probability");
};

// sort & delete
const clickSort = (direction: "up" | "down") => {
  emit("update:sort", { idx: props.idx, direction });
  chatSceneStore.withSaving(async () => {
    await updateOneChatSceneEventSort(props.event.id!, direction);
  });
};

const clickDelete = () => {
  emit("update:delete", props.idx);
};

const saveEvent = (attr: keyof ChatSceneEvent) => {
  if (props.event.id) {
    chatSceneStore.withSaving(async () => {
      await updateOneChatSceneEvent(props.event.id!, {
        [attr]: props.event[attr],
      });
    });
  } else {
    chatSceneStore.withSaving(async () => {
      const { item } = await createOneChatSceneEvent({
        chatSceneId: props.event.chatSceneId,
        [attr]: props.event[attr],
      });
      props.event.id = item.id;
    });
  }
};
</script>
